// src/components/Sidebar/SearchSection.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { searchContent } from '../../api';
import './SearchSection.css';

// Quick searches rather than a real tag taxonomy — the API has no tag index,
// so these run the same query the input would. Kept short and topical instead
// of the previous hardcoded list that did nothing when clicked.
const QUICK_SEARCHES = ['podcast', 'kingston', 'music', 'tech', 'culture', 'business'];

const SearchSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [errorMessage, setErrorMessage] = useState('');
  const abortRef = useRef(null);

  // Abort any in-flight request when the sidebar unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  const runSearch = useCallback(async (query) => {
    const trimmed = (query || '').trim();

    if (!trimmed) {
      setResults(null);
      setStatus('idle');
      return;
    }

    // A slow earlier query must not overwrite a newer one's results.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus('loading');
    setErrorMessage('');

    try {
      const data = await searchContent(trimmed, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setResults(data);
      setStatus('done');
    } catch (error) {
      if (controller.signal.aborted || error.name === 'AbortError') return;
      setErrorMessage('Search is unavailable right now. Please try again.');
      setStatus('error');
    }
  }, []);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    runSearch(searchQuery);
  };

  const handleQuickSearch = (term) => {
    setSearchQuery(term);
    runSearch(term);
  };

  const clearSearch = () => {
    abortRef.current?.abort();
    setSearchQuery('');
    setResults(null);
    setStatus('idle');
  };

  const episodes = results?.episodes ?? [];
  const shows = results?.shows ?? [];
  const hasResults = episodes.length > 0 || shows.length > 0;

  return (
    <div className="search-section">
      <form className="search-bar" onSubmit={handleSearchSubmit} role="search">
        <i className="fas fa-search search-icon" aria-hidden="true"></i>
        <label className="visually-hidden" htmlFor="sidebar-search">Search episodes and shows</label>
        <input
          id="sidebar-search"
          type="search"
          className="search-input"
          placeholder="Search episodes..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
        {searchQuery && (
          <button type="button" className="search-clear" onClick={clearSearch} aria-label="Clear search">
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        )}
      </form>

      {status !== 'idle' && (
        <div className="search-results" aria-live="polite">
          {status === 'loading' && <p className="search-status">Searching…</p>}

          {status === 'error' && <p className="search-status search-status--error">{errorMessage}</p>}

          {status === 'done' && !hasResults && (
            <p className="search-status">No matches for “{searchQuery.trim()}”.</p>
          )}

          {status === 'done' && hasResults && (
            <>
              {episodes.length > 0 && (
                <ul className="search-result-list">
                  {episodes.map((episode) => (
                    <li key={`episode-${episode.id ?? episode.slug}`}>
                      <Link to={`/episode/${episode.slug ?? episode.id}`} className="search-result">
                        <i className="fas fa-microphone-lines" aria-hidden="true"></i>
                        <span className="search-result-title">{episode.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              {shows.length > 0 && (
                <ul className="search-result-list">
                  {shows.map((show) => (
                    <li key={`show-${show.id ?? show.slug}`}>
                      <Link to="/episodes" className="search-result">
                        <i className="fas fa-list" aria-hidden="true"></i>
                        <span className="search-result-title">{show.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      <div className="popular-hashtags">
        <h3>Popular Searches</h3>
        <div className="hashtag-cloud">
          {QUICK_SEARCHES.map((term) => (
            <button
              key={term}
              type="button"
              className="hashtag-item"
              onClick={() => handleQuickSearch(term)}
            >
              #{term}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchSection;
